// Shared classifier for local-dev endpoints. Auth isolation (store/storage.js)
// and the native Release endpoint safety net (constants/config.js) must agree on
// exactly what counts as "local", or a Debug QA session can collide with the
// installed production session. Keep this pure and dependency-free.

// Exact hosts that mean loopback/unspecified. IPv6 entries are stored without
// brackets because WHATWG URL normalization keeps them bracketed in hostname.
const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);

// RFC1918 private ranges plus loopback, matched per octet so prefix lookalikes
// such as `192.168.evil.com` or `172.32.0.1` are not misclassified.
function isLocalIPv4(host) {
  const parts = host.split('.');
  if (parts.length !== 4) return false;
  const octets = [];
  for (const part of parts) {
    if (!/^\d{1,3}$/.test(part)) return false;
    const value = Number(part);
    if (value > 255) return false;
    octets.push(value);
  }
  const [a, b] = octets;
  return (
    a === 127 ||
    a === 10 ||
    (a === 192 && b === 168) ||
    (a === 172 && b >= 16 && b <= 31)
  );
}

// ULA/link-local endpoints need the same credential isolation and Release
// safety net as private IPv4. Mapped IPv4 must retain its underlying scope.
function isLocalIPv6(host) {
  if (!host.includes(':') || !/^[0-9a-f:.]+$/.test(host)) return false;
  let normalized;
  try { normalized = new URL(`http://[${host}]/`).hostname.slice(1, -1); }
  catch { return false; }
  if (normalized === '::1' || normalized === '::') return true;
  const first = Number.parseInt(normalized.split(':')[0], 16);
  if ((first & 0xfe00) === 0xfc00 || (first & 0xffc0) === 0xfe80) return true;
  const mapped = normalized.match(/^::ffff:([0-9a-f]{1,4}):([0-9a-f]{1,4})$/);
  if (!mapped) return false;
  const high = Number.parseInt(mapped[1], 16), low = Number.parseInt(mapped[2], 16);
  const ipv4 = `${high >> 8}.${high & 255}.${low >> 8}.${low & 255}`;
  return LOOPBACK_HOSTNAMES.has(ipv4) || isLocalIPv4(ipv4);
}

/** Classifies an already-parsed hostname (URL.hostname, IPv6 still bracketed). */
export function isLocalHostname(hostname) {
  if (typeof hostname !== 'string' || hostname === '') return false;
  let host = hostname.toLowerCase();
  if (host.startsWith('[') && host.endsWith(']')) {
    host = host.slice(1, -1);
  } else if (host.endsWith('.')) {
    // DNS absolute spelling of a single-label host: `localhost.` === `localhost`,
    // while `localhost.evil.example` keeps its dots and stays remote.
    host = host.slice(0, -1);
  }
  return LOOPBACK_HOSTNAMES.has(host) || isLocalIPv4(host) || isLocalIPv6(host);
}

/**
 * True when `value` is a valid http/https URL whose normalized host is
 * loopback, unspecified, RFC1918, IPv6 ULA/link-local or mapped local IPv4.
 * Expanded IPv6 loopback (`[0:0:0:0:0:0:0:1]`) is
 * collapsed by URL normalization before classification.
 */
export function isLocalEndpoint(value) {
  if (typeof value !== 'string' || value.trim() === '') return false;
  let url;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
  return isLocalHostname(url.hostname);
}
